import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { hashData } from '@confluence/hash';

import {
	CROSSPRODUCT_SEARCH_API,
	CONFLUENCE_SEARCH_API,
	CURRENT,
	ARCHIVED,
	OptionIds,
} from './AdvancedSearch-constants.ts';
import { getOptionSelections, transformFiltersForAnalytics } from './utils/filters.tsx';
import { useSearchInputContext } from './SearchInputProvider.tsx';

type fireAnalyticsArgs = {
	type: string;
	action: string;
	actionSubject: string;
	actionSubjectId?: string;
	additionalAttributes?: object;
};

interface AnalyticsContextType {
	fireAnalytics: (args: fireAnalyticsArgs) => void;
	searchSessionId: string;
}

export const AnalyticsContext: React.Context<AnalyticsContextType> =
	React.createContext<AnalyticsContextType>({
		fireAnalytics: () => {},
		searchSessionId: '',
	});

type AnalyticsProviderProps = React.PropsWithChildren<{
	searchSessionId: string;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
}>;

export const AnalyticsProvider = ({
	searchSessionId,
	createAnalyticsEvent,
	children,
}: AnalyticsProviderProps): React.JSX.Element => {
	const [commonAttributes, setCommonAttributes] = useState<object>({});
	const [privateAttributes, setPrivateAttributes] = useState<object>({});

	const { activationId } = useSessionData();
	const searchInputs = useSearchInputContext();

	useEffect(() => {
		const { searchQuery, filters, cql } = searchInputs;
		const filterSelections = getOptionSelections(filters);
		const filterCategories = transformFiltersForAnalytics(filterSelections);
		const archivedStatus = filterSelections.some(
			(selection) => selection.value === OptionIds.ARCHIVED,
		)
			? [CURRENT, ARCHIVED]
			: [CURRENT];

		setCommonAttributes({
			searchSessionId,
			numberOfTerms: (searchQuery.match(/\w+/g) || []).length,
			queryLength: searchQuery.length,
			queryHash: hashData(searchQuery),
			filters: filterCategories,
			filterSelections,
			containerStatuses: archivedStatus,
			contentStatuses: archivedStatus,
			activationId,
			version: 2,
			api: searchInputs.queryType === 'CQLQuery' ? CONFLUENCE_SEARCH_API : CROSSPRODUCT_SEARCH_API,
			navdexPointType: TOUCH,
			wordCount: (searchQuery.match(/\w+/g) || []).length,
			isCQL: cql !== undefined,
		});
		setPrivateAttributes({
			query: searchQuery,
		});
	}, [searchInputs, searchSessionId, activationId]);

	const fireAnalytics = useCallback(
		({ type, action, actionSubject, actionSubjectId, additionalAttributes }: fireAnalyticsArgs) => {
			createAnalyticsEvent({
				type,
				data: {
					source: 'advancedSearchScreen',
					action,
					actionSubject,
					actionSubjectId,
					nonPrivacySafeAttributes: privateAttributes,
					attributes: { ...commonAttributes, ...additionalAttributes },
				},
			}).fire();
		},
		[commonAttributes, privateAttributes, createAnalyticsEvent],
	);

	const analyticsContextValue = useMemo(
		() => ({
			searchSessionId,
			fireAnalytics,
		}),
		[searchSessionId, fireAnalytics],
	);

	return (
		<AnalyticsContext.Provider value={analyticsContextValue}>{children}</AnalyticsContext.Provider>
	);
};

export const useAnalytics = (): AnalyticsContextType => {
	return useContext(AnalyticsContext);
};
